/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from "@emotion/react";
import React, { FC, ReactNode, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  EventInfo,
  convertMapToValues,
  createSet,
  openWindow,
} from "../../utility";
import { Store, useSelector } from "../../store";
import { canWatchVideo } from "../../utility/streaming";
import { ticketActions } from "../../modules/ticket";
import { filterDisplayVideoTickets } from "utility/ticket";
import type { EventVideo } from "@spwn/types/firebase/firestore";
import { Box, SvgIcon } from "@material-ui/core";
import { ResGetStreamingKey } from "@spwn/types/functions";
import { useGetMainVideosOfVideoGroupsSetsWithoutCurrentSets } from "./useVideoGroupsSetsList";
import { isFeatureEnabled } from "utility/isFeatureEnabled";

/**
 * 配信ページのタイトル下部に表示される、現在視聴中とは別にイベントに紐づいている配信を表示する
 */
export const EventVideoListContainerV2: React.FC<{
  event: EventInfo;
  currentVid: string;
  setWatchVideo: ({ vid, prevVid }: { vid: string; prevVid?: string }) => void;
}> = (props) => {
  const dispatch = useDispatch();

  const isAdmin = useSelector((state: Store) => state.admin.isAdmin);
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const eventTicketMap = useSelector(
    (state: Store) => state.ticket.eventTicketMap
  );
  const eventVideoMap = useSelector(
    (state: Store) => state.streaming.eventVideoMap
  );
  const streamingKey = useSelector(
    (state: Store) => state.streaming.streamingKey
  );

  useEffect(() => {
    dispatch(
      ticketActions.fetchEventTickets.started({ eventId: props.event.eventId })
    );
  }, [dispatch, props.event.eventId]);

  // 必要な情報がない場合はrenderしない
  if (!streamingKey) {
    return null;
  }
  if (eventTicketMap === null || eventVideoMap === null) {
    return null;
  }

  const isOnSale = props.event.eventSaleStatus === "ON_SALE";

  const otherVideos = convertMapToValues(eventVideoMap)
    .filter(
      (video) =>
        createSet(
          filterDisplayVideoTickets(eventTicketMap, isAdmin).flatMap(
            (el) => el.videoIds
          )
        ).includes(video?._id ?? "") && video._id !== props.currentVid
    )
    .sort((a, b) => a.priority - b.priority);

  if (otherVideos.length === 0) {
    return null;
  }

  const handleIconAction = (
    vid: string,
    canWatch: boolean,
    enableAction: boolean
  ) => {
    if (!enableAction) {
      return;
    }
    if (canWatch) {
      props.setWatchVideo({ vid });
      window.scrollTo(0, 0);
      return;
    }
    // if event is on sale
    if (isOnSale) {
      if (props.event.ticketUrl) {
        // if ticket purchase is external site, open its site
        openWindow(props.event.ticketUrl);
        return;
      }
      // go ticket page
      if (
        isFeatureEnabled({
          featureName: "newEventTicketPage",
          eventId: props.event.eventId,
        })
      ) {
        openWindow(`/events/${props.event.eventId}#ticket`);
        return;
      }
      openWindow(`/events/${props.event.eventId}/ticket`);
      return;
    }
    return;
  };

  return (
    <EventVideoList
      isAdmin={isAdmin}
      isDarkModeEnabled={isDarkModeEnabled}
      isOnSale={isOnSale}
      event={props.event}
      eventVideoList={otherVideos}
      streamingKey={streamingKey}
      handleIconAction={handleIconAction}
      currentVideoId={props.currentVid}
    />
  );
};

const EventVideoList: React.FC<{
  isAdmin: boolean;
  isDarkModeEnabled: boolean;
  isOnSale: boolean;
  event: EventInfo;
  eventVideoList: EventVideo[];
  streamingKey: ResGetStreamingKey;
  handleIconAction: (
    vid: string,
    canWatch: boolean,
    enableAction: boolean
  ) => void;
  currentVideoId: string;
}> = ({
  isAdmin,
  isDarkModeEnabled,
  isOnSale,
  event,
  eventVideoList,
  streamingKey,
  handleIconAction,
  currentVideoId,
}) => {
  const videoGroupsSetsList =
    useGetMainVideosOfVideoGroupsSetsWithoutCurrentSets(
      currentVideoId,
      eventVideoList
    );

  return (
    <Box display={"flex"} flexDirection={"column"} gridGap={"12px"}>
      <Box
        component={"h2"}
        color={isDarkModeEnabled ? "dark.text.primary" : "light.text.primary"}
      >
        配信・アーカイブ
      </Box>

      <Box
        component={"ul"}
        display={"flex"}
        flexWrap={"wrap"}
        gridGap={"16px"}
        width={"100%"}
      >
        {[
          /**
           * videoGroupに紐付いていないビデオは表示
           */
          ...eventVideoList.filter(
            (video) => video.videoGroupRef === undefined
          ),
          /**
           * イベントに紐付いているvideoGroupsSetsも表示
           * 中身は、
           */
          ...videoGroupsSetsList,
        ].map((eventVideo) => {
          const vid = eventVideo._id ?? "";
          const videoImg = eventVideo.thumbnail || event.thumbnail;

          const canWatch = canWatchVideo(streamingKey, vid);
          const isOpen = eventVideo.isOpen === undefined || eventVideo.isOpen;
          const enableAnyAction =
            (isOpen && (canWatch || (!canWatch && isOnSale))) || isAdmin;

          if (!enableAnyAction) {
            return null;
          }

          return (
            <EventVideoListItem
              key={eventVideo._id}
              imageUrl={videoImg}
              title={eventVideo.name}
              isLive={!eventVideo.hasVOD}
              isDarkModeEnabled={isDarkModeEnabled}
              canWatch={canWatch}
              isOnSale={isOnSale}
              isStreamingOpen={isOpen}
              onClickChangeVideo={() =>
                handleIconAction(vid, canWatch, enableAnyAction)
              }
            />
          );
        })}
      </Box>
    </Box>
  );
};

export const EventVideoListItem: FC<{
  imageUrl: string;
  title: string;
  isLive: boolean;
  isDarkModeEnabled: boolean;
  canWatch: boolean;
  isOnSale: boolean;
  isStreamingOpen: boolean;
  onClickChangeVideo: () => void;
}> = ({
  imageUrl,
  title,
  isLive,
  isDarkModeEnabled,
  canWatch,
  isOnSale,
  isStreamingOpen,
  onClickChangeVideo,
}) => {
  return (
    <Box
      component={"li"}
      flexShrink={[0, 1]}
      width={"100%"}
      maxWidth={["auto", "320px"]}
      overflow={"hidden"}
      style={{ cursor: "pointer" }}
      onClick={onClickChangeVideo}
    >
      <Box position={"relative"}>
        <img
          src={imageUrl}
          alt={title}
          style={{
            aspectRatio: "16/9",
            objectFit: "cover",
            width: "100%",
            height: "auto",
          }}
        />

        <EventVideoListItemOverlay
          canWatch={canWatch}
          isOnSale={isOnSale}
          isStreamingOpen={isStreamingOpen}
        />
      </Box>

      <Box padding={"16px"}>
        <Box minHeight={"45px"}>
          <Box
            display={"inline-flex"}
            gridGap={"4px"}
            color={
              isDarkModeEnabled ? "dark.text.primary" : "light.text.primary"
            }
          >
            {isLive && (
              <LiveIcon fill={isDarkModeEnabled ? "#ffffff" : "#000000"} />
            )}{" "}
            {title}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const EventVideoListItemOverlay: FC<{
  canWatch: boolean;
  isOnSale: boolean;
  isStreamingOpen: boolean;
}> = ({ canWatch, isOnSale, isStreamingOpen }) => {
  if (canWatch) {
    if (isStreamingOpen) {
      return null;
    } else {
      return (
        <EventVideoListItemOverlayLayout>
          配信期間外です
        </EventVideoListItemOverlayLayout>
      );
    }
  } else {
    if (isOnSale) {
      return (
        <EventVideoListItemOverlayLayout>
          <Box>チケットを持っていません</Box>

          <Box>
            <button
              css={css`
                display: inline-block;
                padding: 6px 12px;
                border-radius: 4px;
                font-size: 14px;
                font-weight: bold;
                border: none;
                background-color: #fff;
                cursor: pointer;
              `}
            >
              チケットを購入する
            </button>
          </Box>
        </EventVideoListItemOverlayLayout>
      );
    } else {
      return (
        <EventVideoListItemOverlayLayout>
          販売終了しました
        </EventVideoListItemOverlayLayout>
      );
    }
  }
};

const EventVideoListItemOverlayLayout: FC<{
  children: ReactNode;
}> = ({ children }) => {
  return (
    <Box
      position={"absolute"}
      top={0}
      left={0}
      right={0}
      bottom={0}
      display={"flex"}
      flexDirection={"column"}
      gridGap={"8px"}
      alignItems={"center"}
      justifyContent={"center"}
      color={"#fff"}
      fontSize={"14px"}
      bgcolor={"rgba(0, 0, 0, 0.5)"}
    >
      {children}
    </Box>
  );
};

const LiveIcon: FC<{ fill: string }> = ({ fill }) => {
  return (
    <SvgIcon
      viewBox="0 0 24 25"
      width={"24px"}
      height={"25px"}
      data-testid="live-icon"
    >
      <path
        d="M7.76 16.7397C6.67 15.6597 6 14.1597 6 12.4997C6 10.8397 6.67 9.33969 7.76 8.25969L9.18 9.67969C8.45 10.3997 8 11.3997 8 12.4997C8 13.5997 8.45 14.5997 9.17 15.3297L7.76 16.7397ZM16.24 16.7397C17.33 15.6597 18 14.1597 18 12.4997C18 10.8397 17.33 9.33969 16.24 8.25969L14.82 9.67969C15.55 10.3997 16 11.3997 16 12.4997C16 13.5997 15.55 14.5997 14.83 15.3297L16.24 16.7397ZM12 10.4997C10.9 10.4997 10 11.3997 10 12.4997C10 13.5997 10.9 14.4997 12 14.4997C13.1 14.4997 14 13.5997 14 12.4997C14 11.3997 13.1 10.4997 12 10.4997ZM20 12.4997C20 14.7097 19.1 16.7097 17.65 18.1497L19.07 19.5697C20.88 17.7597 22 15.2597 22 12.4997C22 9.73969 20.88 7.23969 19.07 5.42969L17.65 6.84969C18.396 7.58919 18.9878 8.46936 19.3912 9.43921C19.7945 10.4091 20.0015 11.4493 20 12.4997ZM6.35 6.84969L4.93 5.42969C3.12 7.23969 2 9.73969 2 12.4997C2 15.2597 3.12 17.7597 4.93 19.5697L6.35 18.1497C4.9 16.7097 4 14.7097 4 12.4997C4 10.2897 4.9 8.28969 6.35 6.84969Z"
        fill={fill}
      />
    </SvgIcon>
  );
};
